import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { apiJson } from '../api/client.js';
import { FormInput } from './FormControls.jsx';
import { SearchableSelect } from './SearchableSelect.jsx';

export function AdminChatSettings() {
  const [form, setForm] = useState({
    enabled: false,
    guest_chat_enabled: true,
    invitation_mode: 'queue',
    ai_enabled: false,
    ai_fallback_seconds: 120,
    invite_email_subject: '',
    invite_email_body: '',
  });
  const [openaiConfigured, setOpenaiConfigured] = useState(false);
  const [orgId, setOrgId] = useState(null);
  const [msg, setMsg] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    apiJson('/api/admin/organization').then((o) => setOrgId(o.id)).catch(() => {});
    apiJson('/api/admin/chat/settings')
      .then((s) => {
        setForm({
          enabled: s.enabled,
          guest_chat_enabled: s.guest_chat_enabled,
          invitation_mode: s.invitation_mode || 'queue',
          ai_enabled: s.ai_enabled,
          ai_fallback_seconds: s.ai_fallback_seconds ?? 120,
          invite_email_subject: s.invite_email_subject || '',
          invite_email_body: s.invite_email_body || '',
        });
        setOpenaiConfigured(s.openai_configured);
      })
      .catch((e) => setError(e.message));
  }, []);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    setMsg('');
    try {
      const updated = await apiJson('/api/admin/chat/settings', { method: 'PUT', body: form });
      setOpenaiConfigured(updated.openai_configured);
      setMsg('Settings saved.');
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const guestLink =
    orgId && typeof window !== 'undefined' ? `${window.location.origin}/chat/guest/${orgId}` : null;

  return (
    <div>
      {error ? <p className="err">{error}</p> : null}
      {msg ? <p className="muted">{msg}</p> : null}
      <form onSubmit={save} className="stack">
        <label className="field row-actions">
          <input
            type="checkbox"
            checked={form.enabled}
            onChange={(e) => setForm((f) => ({ ...f, enabled: e.target.checked }))}
          />
          <span>
            <strong>Enable live chat</strong>
            <span className="muted small" style={{ display: 'block' }}>
              When on, clients see a chat button in the portal and staff can respond here.
            </span>
          </span>
        </label>
        <label className="field row-actions">
          <input
            type="checkbox"
            checked={form.guest_chat_enabled}
            onChange={(e) => setForm((f) => ({ ...f, guest_chat_enabled: e.target.checked }))}
            disabled={!form.enabled}
          />
          <span>Allow guest (unauthenticated) chat</span>
        </label>
        <label className="field">
          <span>Offline invite mode</span>
          <SearchableSelect
            className="form-control"
            value={form.invitation_mode}
            onChange={(e) => setForm((f) => ({ ...f, invitation_mode: e.target.value }))}
            disabled={!form.enabled}
          >
            <option value="queue">Round-robin (one manager per invite)</option>
            <option value="all">Notify all managers</option>
          </SearchableSelect>
        </label>
        <label className="field">
          <span>Invite email subject</span>
          <FormInput
            value={form.invite_email_subject}
            onChange={(e) => setForm((f) => ({ ...f, invite_email_subject: e.target.value }))}
            placeholder="New chat waiting"
            disabled={!form.enabled}
          />
        </label>
        <label className="field">
          <span>Invite email body</span>
          <textarea
            className="form-control"
            rows={4}
            value={form.invite_email_body}
            onChange={(e) => setForm((f) => ({ ...f, invite_email_body: e.target.value }))}
            placeholder="Use {chat_url}, {manager_name}, {conversation_id}"
            disabled={!form.enabled}
          />
        </label>
        <hr />
        <h4>AI assistant</h4>
        <p className="muted small">
          OpenAI API key is configured in{' '}
          <Link to="/admin/integrations">Integrations → OpenAI</Link>.
          {openaiConfigured ? ' Key is saved.' : ' No key saved yet.'}
        </p>
        <label className="field row-actions">
          <input
            type="checkbox"
            checked={form.ai_enabled}
            onChange={(e) => setForm((f) => ({ ...f, ai_enabled: e.target.checked }))}
            disabled={!form.enabled || !openaiConfigured}
          />
          <span>Enable AI when staff does not reply in time</span>
        </label>
        <label className="field">
          <span>AI fallback delay (seconds)</span>
          <FormInput
            type="number"
            min={30}
            value={form.ai_fallback_seconds}
            onChange={(e) => setForm((f) => ({ ...f, ai_fallback_seconds: parseInt(e.target.value, 10) || 120 }))}
            disabled={!form.enabled || !form.ai_enabled}
          />
        </label>
        <button type="submit" className="btn-primary" disabled={saving}>
          {saving ? 'Saving…' : 'Save settings'}
        </button>
      </form>
      {guestLink && form.enabled && form.guest_chat_enabled ? (
        <p className="muted small" style={{ marginTop: 12 }}>
          Guest chat URL: <a href={guestLink}>{guestLink}</a>
        </p>
      ) : null}
    </div>
  );
}
